import React, { useState } from "react";
import { Folder, File as FileIcon, MoreHorizontal, Loader2 } from "lucide-react";
import { UIRoomItem } from "../../../models/RoomItem";
import { Util } from "../../../services/Utils/Util";
import { Checkbox } from "../../../components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { cn } from "../../../lib/utils";
import { Fire } from "../../../services/FireClient";
import { useToast } from "../../../components/ui/use-toast";
import { t } from "@lingui/macro";

import { ItemPopover } from "../../components/ItemPopover";

interface FileGridItemProps {
    uitem: UIRoomItem;
    checked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    onMoveFolder?: () => void;
    onPreview?: (url: string) => void;
    canEdit?: boolean;
}

// 파일 확장자 추출 (최대 4글자)
function getFileExtension(filename: string | undefined): string {
    if (!filename) return '';
    const parts = filename.split('.');
    if (parts.length < 2) return '';
    const ext = parts[parts.length - 1].toUpperCase();
    return ext.length <= 4 ? ext : ext.substring(0, 4);
}

// 확장자별 배경색
function getExtensionColor(ext: string): string {
    const colors: { [key: string]: string } = {
        'PDF': 'bg-red-500',
        'DOC': 'bg-blue-500',
        'DOCX': 'bg-blue-500',
        'XLS': 'bg-green-600',
        'XLSX': 'bg-green-600',
        'PPT': 'bg-orange-500',
        'PPTX': 'bg-orange-500',
        'ZIP': 'bg-yellow-600',
        'RAR': 'bg-yellow-600',
        'TXT': 'bg-gray-500',
        'CSV': 'bg-green-500',
        'MP3': 'bg-purple-500',
        'MP4': 'bg-pink-500',
        'MOV': 'bg-pink-500',
    };
    return colors[ext] || 'bg-gray-400';
}

export const FileGridItem: React.FC<FileGridItemProps> = ({ uitem, checked, onCheckedChange, onMoveFolder, onPreview, canEdit }) => {
    const item = uitem.item;
    const isFolder = item.type === 'folder';
    const isMessageFolder = (uitem.itemType as string) === 'MessageFolder';
    const isImage = item.url && Util.isImageUrl(item.url);
    const ext = getFileExtension(item.title || item.file?.filename);
    const extColor = getExtensionColor(ext);
    const { toast } = useToast();
    const [isOpening, setIsOpening] = useState(false);

    const handleOpen = async () => {
        if (isFolder) {
            onMoveFolder?.();
            return;
        }

        setIsOpening(true);
        try {
            const res = await Fire.item.getRoomItemDownloadURL(item.roomId, item.id);
            if (res.status === 'SUCCESS' && res.url) {
                // Server sets responseDisposition: attachment, so this will trigger download
                window.open(res.url, '_blank');
            } else {
                toast({
                    variant: "destructive",
                    title: t`Download Failed`,
                    description: res.status === 'NOT_MEMBER' ? t`You don't have permission.` : t`File not found or expired.`,
                });
            }
        } catch (error) {
            console.error("Failed to get download URL", error);
            toast({
                variant: "destructive",
                title: t`Error`,
                description: (error as Error).message,
            });
        } finally {
            setIsOpening(false);
        }
    };

    return (
        <div className="relative group bg-white border border-gray-100 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
            <div className="absolute top-2 left-2 z-10">
                <Checkbox
                    checked={checked}
                    onCheckedChange={onCheckedChange}
                    disabled={isMessageFolder}
                    className="bg-white/80 border-gray-300 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                />
            </div>

            <div
                className={cn("aspect-square flex items-center justify-center bg-gray-50 cursor-pointer overflow-hidden relative", isFolder && "text-orange-400")}
                onClick={handleOpen}
            >
                {isFolder ? (
                    <Folder className="w-16 h-16 fill-current" />
                ) : isImage ? (
                    <img src={item.url} className="w-full h-full object-cover transition-transform group-hover:scale-105" alt={item.title} />
                ) : (
                    <div className="relative flex flex-col items-center justify-center">
                        <FileIcon className="w-12 h-12 text-gray-300" />
                        {ext && (
                            <span className={cn(
                                "absolute bottom-2 px-2 py-0.5 text-[10px] font-bold text-white rounded",
                                extColor
                            )}>
                                {ext}
                            </span>
                        )}
                    </div>
                )}
                {isOpening && (
                    <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                        <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
                    </div>
                )}
            </div>

            <div className="p-3">
                <div
                    className="font-medium text-gray-900 text-sm truncate mb-1 cursor-pointer hover:text-orange-600"
                    onClick={handleOpen}
                >
                    {item.title || item.url}
                </div>
                {!isMessageFolder && (
                    <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1.5 overflow-hidden">
                            <Avatar className="w-4 h-4">
                                <AvatarImage src={item.user.photoUrl ?? Util.ANONYMOUS_PHOTO_URL} />
                                <AvatarFallback>?</AvatarFallback>
                            </Avatar>
                            <span className="text-[10px] text-gray-500 truncate">{item.user.name}</span>
                        </div>
                        <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <ItemPopover item={item} canEdit={!!canEdit}>
                                <button className="p-1 hover:bg-gray-100 rounded-full bg-white shadow-sm border border-gray-200" onClick={(e) => e.stopPropagation()}>
                                    <MoreHorizontal className="w-4 h-4 text-gray-500" />
                                </button>
                            </ItemPopover>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
